import {
  FactCheckOutlined,
  FenceOutlined,
  ImageOutlined,
  LayersOutlined,
  LightbulbOutlined,
  MonitorHeartOutlined,
  MyLocationOutlined,
  NfcOutlined,
  SvgIconComponent,
  TouchAppOutlined,
  VerifiedUserOutlined,
} from '@mui/icons-material';
import { CapabilityKey } from './types';

/**
 * Display metadata for each capability — used by chips, tooltips and the
 * capability preview in the add/edit form. Kept as a typed constant so the
 * list/form/details surfaces stay consistent.
 */
export interface CapabilityMeta {
  key: CapabilityKey;
  /** Short badge label (e.g. `Img`). */
  abbr: string;
  /** Human-readable label (e.g. `Image display`). */
  label: string;
  /** One-line explanation for tooltips. */
  description: string;
  icon: SvgIconComponent;
}

export const CAPABILITY_META: Record<CapabilityKey, CapabilityMeta> = {
  ImageDisplay: {
    key: 'ImageDisplay',
    abbr: 'Img',
    label: 'Image display',
    description: "Push a rendered image to the device's screen (and clear it).",
    icon: ImageOutlined,
  },
  Pages: {
    key: 'Pages',
    abbr: 'Pg',
    label: 'Pages',
    description: 'Device has multiple display pages; switch now or schedule a switch.',
    icon: LayersOutlined,
  },
  NFCTag: {
    key: 'NFCTag',
    abbr: 'NFC',
    label: 'NFC tag',
    description: 'Write/clear NFC content on the device.',
    icon: NfcOutlined,
  },
  LightNotification: {
    key: 'LightNotification',
    abbr: 'Lgt',
    label: 'Light notification',
    description: 'Blink an LED in a color for a duration (e.g. to guide a picker).',
    icon: LightbulbOutlined,
  },
  ButtonEvent: {
    key: 'ButtonEvent',
    abbr: 'Btn',
    label: 'Button event',
    description: 'Device has physical buttons and emits press events.',
    icon: TouchAppOutlined,
  },
  DeviceStatus: {
    key: 'DeviceStatus',
    abbr: 'DSt',
    label: 'Device status',
    description: 'Per-device health: online/offline, battery, signal, current page, firmware.',
    icon: MonitorHeartOutlined,
  },
  ServiceStatus: {
    key: 'ServiceStatus',
    abbr: 'SSt',
    label: 'Service status',
    description: 'Appliance/service-level health plus aggregate statistics.',
    icon: FactCheckOutlined,
  },
  DeviceIdValidation: {
    key: 'DeviceIdValidation',
    abbr: 'IdV',
    label: 'Device-ID validation',
    description: 'Validate a device id / template compatibility before linking.',
    icon: VerifiedUserOutlined,
  },
  Trackable: {
    key: 'Trackable',
    abbr: 'Trk',
    label: 'Trackable',
    description: 'Real-time location tracking (omlox).',
    icon: MyLocationOutlined,
  },
  GeoFencing: {
    key: 'GeoFencing',
    abbr: 'Geo',
    label: 'Geofencing',
    description: 'Fence entry and exit events update the compartment and can trigger event rules.',
    icon: FenceOutlined,
  },
};

/** Stable display order for capability chips. */
export const CAPABILITY_ORDER: CapabilityKey[] = [
  'ImageDisplay',
  'Pages',
  'NFCTag',
  'LightNotification',
  'ButtonEvent',
  'DeviceStatus',
  'ServiceStatus',
  'DeviceIdValidation',
  'Trackable',
  'GeoFencing',
];

const CAPABILITY_KEYS = new Set<string>(CAPABILITY_ORDER);

const isCapabilityKey = (value: string): value is CapabilityKey => CAPABILITY_KEYS.has(value);

/** Order a set of capability keys by the canonical display order. */
export const orderCapabilities = (keys: CapabilityKey[]): CapabilityKey[] => {
  const present = new Set<CapabilityKey>(keys);
  return CAPABILITY_ORDER.filter((key) => present.has(key));
};

/**
 * Parse the SDK's comma-separated `DeviceIntegrationDescription.capabilities`
 * string into ordered, known capability keys (unknown tokens are dropped).
 */
export const parseCapabilities = (csv: string): CapabilityKey[] =>
  orderCapabilities(
    csv
      .split(',')
      .map((token) => token.trim())
      .filter(isCapabilityKey),
  );

/** Serialize capability keys back into the SDK's comma-separated string. */
export const serializeCapabilities = (keys: CapabilityKey[]): string => orderCapabilities(keys).join(',');
